import {
  type Component,
  type PresentationCurrent,
  type Purpose,
  type TextValue,
} from '@/api/core/native-presentation';
import { languageLabel } from '../media/media-presentation';
import {
  CATEGORY_ORDER,
  CATEGORY_TITLE,
  COMPONENT_CATEGORY,
  KIND_TITLE,
  PURPOSE_CATEGORY,
  PURPOSE_ORDER,
  purposeLabel,
  type CategoryId,
} from './presentation-categories';

const LANGUAGES: TextValue['language'][] = ['ko', 'en', 'und'];
const valueText = (value: string | null | undefined) =>
  value === null || value === undefined ? '미설정' : value === '' ? '빈 문구' : value;

interface TextRow {
  key: string;
  purpose: Purpose;
  values: Partial<Record<TextValue['language'], string | null>>;
}

/** (용도, 항목 키)가 같은 문구는 같은 항목의 언어별 값이다. */
function groupRows(texts: TextValue[]): TextRow[] {
  const rows = new Map<string, TextRow>();
  for (const text of texts) {
    const key = `${text.purpose}:${text.item_key}`;
    const row = rows.get(key) ?? { key, purpose: text.purpose, values: {} };
    row.values[text.language] = text.value;
    rows.set(key, row);
  }
  return [...rows.values()].sort(
    (a, b) => PURPOSE_ORDER.indexOf(a.purpose) - PURPOSE_ORDER.indexOf(b.purpose),
  );
}

function Languages({ values }: { values: TextRow['values'] }) {
  const present = LANGUAGES.filter((language) => language in values);
  // 한쪽 언어만 있으면 비어 있는 쪽을 "미설정"으로 드러내 번역이 빠진 곳을 알아보게 한다.
  const shown = present.some((l) => l !== 'und')
    ? LANGUAGES.filter((language) => language !== 'und' || present.includes('und'))
    : present;
  return (
    <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
      {shown.map((language) => (
        <div key={language} className="min-w-0">
          <p className="caption text-muted-foreground">{languageLabel(language)}</p>
          <p className="body break-words whitespace-pre-wrap">{valueText(values[language])}</p>
        </div>
      ))}
    </div>
  );
}

function TextRows({ rows, repeatedLabel }: { rows: TextRow[]; repeatedLabel?: string }) {
  return (
    <dl className="space-y-4">
      {rows.map((row) => (
        <div key={row.key} className="space-y-1">
          {purposeLabel(row.purpose) !== repeatedLabel && (
            <dt className="body-sm font-medium">{purposeLabel(row.purpose)}</dt>
          )}
          <dd>
            <Languages values={row.values} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

function flagNotes(component: Component) {
  const notes: string[] = [];
  if (component.is_active === false) notes.push('사용 안 함');
  if (component.show_on_detail === false) notes.push('상세 숨김');
  if (component.show_on_search === false) notes.push('목록 숨김');
  return notes;
}

function ComponentCard({
  component,
  texts,
  lines,
  title,
}: {
  component: Component;
  texts: TextValue[];
  lines: Component[];
  title: string;
}) {
  const notes = flagNotes(component);
  const rows = groupRows(texts);
  return (
    <article className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h5 className="body-strong">{title}</h5>
        {notes.map((note) => (
          <span key={note} className="caption text-muted-foreground rounded border px-1.5">
            {note}
          </span>
        ))}
      </div>
      {rows.length > 0 && <TextRows rows={rows} repeatedLabel={title} />}
      {lines.length > 0 && (
        <p className="body-sm">
          <span className="text-muted-foreground">노선 </span>
          {lines.map((line) => line.line_code ?? '미설정').join(', ')}
        </p>
      )}
    </article>
  );
}

/** 현재 표시 콘텐츠를 범주 제목 아래 섹션으로 나눠 보여 준다. 값이 없는 범주는 만들지 않는다. */
export function PresentationSummary({ current }: { current: PresentationCurrent }) {
  const componentKeys = new Set(current.components.map((c) => c.key).filter(Boolean));
  const loose = current.texts.filter(
    (t) => !t.component_key || !componentKeys.has(t.component_key),
  );
  const byCategory = new Map<CategoryId, TextValue[]>();
  for (const text of loose) {
    const category = PURPOSE_CATEGORY[text.purpose] ?? 'etc';
    byCategory.set(category, [...(byCategory.get(category) ?? []), text]);
  }
  const textsOf = (component: Component) =>
    current.texts.filter((t) => component.key !== null && t.component_key === component.key);
  const childrenOf = (component: Component) =>
    current.components.filter(
      (c) => c.kind === 'LOCATION_LINE' && component.key !== null && c.parent_key === component.key,
    );
  const topLevel = current.components.filter(
    (c) => !(c.kind === 'LOCATION_LINE' && c.parent_key && componentKeys.has(c.parent_key)),
  );
  const nameOf = (component: Component) => {
    const rows = groupRows(textsOf(component));
    const name = rows.find((r) => r.purpose === 'COMPONENT_NAME' || r.purpose === 'STATION_NAME');
    return name
      ? (name.values.ko ?? name.values.en ?? name.values.und ?? KIND_TITLE[component.kind])
      : KIND_TITLE[component.kind];
  };
  const sections = CATEGORY_ORDER.map((id) => {
    const rows = groupRows(byCategory.get(id) ?? []);
    const components = topLevel.filter((c) => COMPONENT_CATEGORY[c.kind] === id);
    return { id, rows, components };
  }).filter((section) => section.rows.length > 0 || section.components.length > 0);
  if (!sections.length) return <p>등록된 표시 문구가 없습니다.</p>;
  return (
    <div className="space-y-6" aria-label="현재 표시 콘텐츠">
      {sections.map((section) => (
        <section key={section.id} aria-labelledby={`pres-cat-${section.id}`} className="space-y-3">
          <h4 id={`pres-cat-${section.id}`} className="title-sm border-b pb-1">
            {CATEGORY_TITLE[section.id]}
          </h4>
          {section.rows.length > 0 && <TextRows rows={section.rows} />}
          {section.components.length > 0 && (
            <div className="grid gap-3 lg:grid-cols-2">
              {section.components.map((component, index) => (
                <ComponentCard
                  key={component.key ?? String(index)}
                  component={component}
                  texts={textsOf(component).filter(
                    (t) => t.purpose !== 'COMPONENT_NAME' && t.purpose !== 'STATION_NAME',
                  )}
                  lines={childrenOf(component)}
                  title={nameOf(component)}
                />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
